/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { useEffect, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { UpcomingOffer, UpcomingOfferV2 } from 'client-code/upcoming-offers';

import { BrandingUiInfoKeys } from '../../../core/entities';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { RootState, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { TOfferPreviewCustomData } from '../../../core/types';
import { DateTime, UniqueIdentifier } from '../../../core/utils';
import { Mousetrap, ScreenPreview } from '../../common';
import { RecordingStatusIcon } from '../../Guide/RecordingStatusIcon';
import { Preview } from '../../MyShows/Preview';

import './UpcomingList.scss';

interface UpcomingListProps {
    items: UpcomingOffer[] | any;
    selectedIndex?: number;
    ghostMode?: boolean;
}

const UpcomingList: React.FC<UpcomingListProps> = ({ items, selectedIndex, ghostMode }) => {
    const TAG = 'UpcomingList';
    const services = new ServicesClientImpl();
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
    const { recordingsStatusMap } = useTivoSelector((state: RootState) => state.recordingsStatus);
    const upcomingItems = React.useRef<any>(items || []);

    const [previewData, setPreviewData] = useState<TOfferPreviewCustomData | undefined>(undefined);

    const formatDate = (startTime: string): string => {
        const dateTime = new DateTime(startTime);
        return dateTime ? dateTime.strftime('%M/%D') : '';
    };

    const formatTime = (startTime: string): string => {
        const dateTime = new DateTime(startTime);
        return dateTime ? dateTime.strftime('%l:%i%p') : '';
    };

    const getRecordingStatusIcon = async (item: UpcomingOffer): Promise<string> => {
        const getChannelData = await services.getChannelFromChannelNumber(
            item.channel.channelNumber,
        );

        const { offerId } = item;
        const recordingStatus = recordingsStatusMap.get(`${getChannelData.stationId}${offerId}`);
        return recordingStatus || '';
    };

    const getResolutionIcon = (resolution: string): string => {
        switch (resolution.toLowerCase()) {
            case 'hd':
                return 'HD';
            case 'sd':
                return 'SD';
            case '4k':
            case 'uhd':
                return '4K';
            default:
                return '';
        }
    };

    const formatEpisodeTitle = (item: UpcomingOffer | UpcomingOfferV2): string => {
        const { episodeTitle, episodeNumber, seasonNumber, title } = item as any;

        if (seasonNumber && episodeNumber) {
            return `S${seasonNumber} E${episodeNumber} '${episodeTitle || title}'`;
        }
        if (seasonNumber) {
            return `S${seasonNumber} '${episodeTitle || title}'`;
        }

        return episodeTitle || title || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE);
    };

    const getPreview = async () => {
        try {
            const res = await services.fetchOfferPreview(
                items[selectedIndex || 0].offerId,
                'actionScreenUpcomingList',
                UniqueIdentifier.getNextUpcomingNetworkCallId(),
            );

            if (res.networkCallId === UniqueIdentifier.getUpcomingNetworkCallId())
                setPreviewData(res.offerPreview);
        } catch (_error) {
            const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
            Log.e(TAG, errorMessage);
            Diagnostics.error(TAG, errorMessage);
        }
    };

    useEffect(() => {
        getPreview();

        return () => {
            setPreviewData(undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, selectedIndex]);

    useEffect(() => {
        const updateItemsWithRecordingStatus = async () => {
            const updatedItems = await Promise.all(
                items.map(async (item: UpcomingOffer | UpcomingOfferV2) => {
                    const recordingStatus = await getRecordingStatusIcon(item);
                    // If recording status is present, append it to the item, else ignore
                    if (recordingStatus) {
                        return { ...item, recordingStatus };
                    }
                    return item;
                }),
            );

            upcomingItems.current = updatedItems;
        };

        updateItemsWithRecordingStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    return (
        <div className="upcoming-list">
            <div className="upcoming-list-content">
                {upcomingItems?.current.map((item, index) => (
                    <Mousetrap
                        value={`${index}_list-view`}
                        key={`${item.id}-${index}`}
                        className={`upcoming-list-item ${index === selectedIndex ? ` focused${ghostMode ? ' ghostHighlight' : ''}` : ''}`}
                    >
                        <div className="upcoming-list-item-content">
                            <div className="item-date">{formatDate(item.startTime)}</div>

                            <div className="item-time">{formatTime(item.startTime)}</div>

                            <div className="item-content">
                                <span className="item-title">{formatEpisodeTitle(item)}</span>
                                {item.isNew && (
                                    <img
                                        src={newIcon?.imageUrl}
                                        className="icon-new"
                                        data-testid={'program-cell-new-icon'}
                                    />
                                )}
                            </div>

                            <div className="item-channel-info">
                                <span className="channel-number">{item.channel.channelNumber}</span>
                                {getResolutionIcon(item.videoResolution || '') && (
                                    <span className="resolution-badge">
                                        {item.videoResolution && (
                                            <span>
                                                {getResolutionIcon(item.videoResolution || '')}
                                            </span>
                                        )}
                                    </span>
                                )}
                                <div className="additional-icons">
                                    {item.recordingStatus && (
                                        <RecordingStatusIcon type={item.recordingStatus} />
                                    )}
                                </div>
                            </div>
                        </div>
                        <ScreenPreview classStyle="upcoming-list-preview">
                            {previewData && <Preview offer={previewData} />}
                        </ScreenPreview>
                    </Mousetrap>
                ))}
            </div>
        </div>
    );
};

export default UpcomingList;
